/**
 * Launcher-in-toolbar for the dashboard's idle state: project/branch
 * selectors, view-mode toggle, and Launch. Login status intentionally
 * lives in the LoginsPanel below, not here — the selectors need the width.
 * Owns the launch flow end-to-end; rendered only when no workspace is active.
 */

import { useState, type ReactElement } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { GitProject } from '@features/git/types/git.types';
import { Toolbar } from '@components/ui/Toolbar';
import { ROUTES, STORAGE_KEYS } from '@utils/constants';
import { ProjectSelector } from './ProjectSelector';
import { BranchSelector } from './BranchSelector';
import { ViewModeToggle, type WorkspaceViewMode } from './ViewModeToggle';
import { useCreateWorkspace } from '../hooks/useWorkspaces';
import { useGitServers } from '../../git/hooks/useGitServers';

export function LauncherToolbar(): ReactElement {
  const navigate = useNavigate();
  const [selectedProject, setSelectedProject] = useState<GitProject | null>(null);
  const [selectedBranch, setSelectedBranch] = useState<string | null>(null);
  const [launchError, setLaunchError] = useState<string | null>(null);
  const [launchMode, setLaunchMode] = useState<WorkspaceViewMode>(() => {
    const stored = localStorage.getItem(STORAGE_KEYS.WORKSPACE_VIEW_MODE);
    return stored === 'terminal' ? 'terminal' : 'ide';
  });

  const { mutateAsync: createWorkspace, isPending: isCreating } = useCreateWorkspace();
  const { data: serversData } = useGitServers();
  // Agent runs exist only on GitLab: the project page answers "not found" for any other server.
  const onGitLab =
    selectedProject !== null &&
    serversData?.servers.find((s) => s.id === selectedProject.server_id)?.provider_type === 'gitlab';

  const handleProjectSelect = (project: GitProject | null): void => {
    setSelectedProject(project);
    if (project?.default_branch) {
      setSelectedBranch(project.default_branch);
    } else {
      setSelectedBranch(null);
    }
    setLaunchError(null);
  };

  const handleLaunch = async (): Promise<void> => {
    if (!selectedProject || !selectedBranch) return;

    setLaunchError(null);
    localStorage.setItem(STORAGE_KEYS.WORKSPACE_VIEW_MODE, launchMode);
    try {
      const workspace = await createWorkspace({
        project_id: selectedProject.id,
        branch: selectedBranch,
      });
      setSelectedProject(null);
      setSelectedBranch(null);
      void navigate(`${ROUTES.WORKSPACE}/${workspace.id}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create workspace';
      setLaunchError(message);
    }
  };

  const canLaunch = selectedProject !== null && selectedBranch !== null && !isCreating;

  return (
    <>
      {launchError && <div className="error-message">{launchError}</div>}
      <Toolbar
        info={<>
          <div className="app-toolbar__select app-toolbar__select--grow">
            <ProjectSelector
              selectedProject={selectedProject}
              onSelect={handleProjectSelect}
              disabled={isCreating}
            />
          </div>
          {selectedProject && onGitLab && (
            <Link
              to={`${ROUTES.PROJECT}/${selectedProject.id}`}
              className="app-toolbar__link"
              title={`Agent runs for ${selectedProject.path}`}
            >
              Project page
            </Link>
          )}
          <div className="app-toolbar__select app-toolbar__select--branch">
            <BranchSelector
              serverId={selectedProject?.server_id ?? null}
              externalRef={selectedProject?.external_ref ?? null}
              selectedBranch={selectedBranch}
              onSelect={setSelectedBranch}
              disabled={isCreating}
            />
          </div>
          <ViewModeToggle
            value={launchMode}
            onChange={setLaunchMode}
            disabled={isCreating}
            showBrowser={false}
          />
          <button
            type="button"
            onClick={() => { void handleLaunch(); }}
            disabled={!canLaunch}
            className="app-toolbar__primary"
          >
            {isCreating ? 'Launching...' : 'Launch'}
          </button>
        </>}
      />
      <p className="dashboard-launcher__hint">
        Select a project and branch to start your development session
      </p>
    </>
  );
}
